import React from 'react';
import { useRecordingSession } from '@hooks/useRecordingSession';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import { Box, Checkbox, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { Task } from '@ts/types';

interface OnboardingTasksProps {
  tasks: Task[];
  userMetrics?: {
    notes_created?: number;
  } | null;
  onTaskClick?: (task: Task) => void;
}

/**
 * OnboardingTasks Component
 *
 * A clean, modern task list UI component for the Dashboard V2.
 * Displays onboarding tasks with purple-themed checkbox styling.
 */
export const OnboardingTasks: React.FC<OnboardingTasksProps> = ({
  tasks,
  userMetrics,
  onTaskClick,
}) => {
  const { dashboard } = useEventTracking();
  const {
    startRecording,
    canStartRecording,
    isLoadingAppointments,
    requiresScheduledAppointmentForNoteCreation,
  } = useRecordingSession();
  const recordDisabled =
    requiresScheduledAppointmentForNoteCreation && (isLoadingAppointments || !canStartRecording);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tasks.map(task => {
        // Check if this is a task that should navigate to record page
        const isRecordNavigationTask =
          task.title.toLowerCase().includes('assign a patient') ||
          task.title.toLowerCase().includes('record 15 encounters') ||
          task.title.toLowerCase().includes('record your first encounter');

        const isClickable =
          task.task_id === 'consent_information' ||
          (task.video_link && task.video_link !== '') ||
          (isRecordNavigationTask && !recordDisabled);

        return (
          <Box
            key={task.task_id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: isClickable && onTaskClick ? 'pointer' : 'default',
              '&:hover':
                isClickable && onTaskClick
                  ? {
                      opacity: 0.8,
                    }
                  : {},
            }}
            onClick={() => {
              if (isRecordNavigationTask) {
                if (recordDisabled) return;
                // Track the navigation action
                dashboard.actionClicked({
                  action_type: 'onboarding_task_navigate',
                  source: 'onboarding_card',
                });
                // Navigate to record/encounter page
                startRecording();
              } else if (isClickable && onTaskClick) {
                onTaskClick(task);
              }
            }}
          >
            <Checkbox
              icon={
                <CircleIcon
                  sx={{
                    color: 'white',
                    stroke: '#7A7A7A',
                    strokeWidth: 1,
                  }}
                />
              }
              checkedIcon={
                <CheckCircleIcon
                  sx={{
                    color: 'primary.main',
                  }}
                />
              }
              checked={task.is_completed}
              disabled
              sx={{
                padding: 0,
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem',
                },
                '&.Mui-disabled': {
                  color: '#7A7A7A',
                },
              }}
              onClick={e => e.stopPropagation()}
            />
            <Typography
              variant="body2"
              sx={{
                color: task.is_completed ? 'text.disabled' : 'text.primary',
                textDecoration: task.is_completed ? 'line-through' : 'none',
              }}
            >
              {task.title}
              {task.task_id.includes('first_n_notes_created') &&
                userMetrics?.notes_created !== undefined &&
                ` (${userMetrics.notes_created}/${task.target_value})`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default OnboardingTasks;
